<!-- eslint-disable vue/v-slot-style, vue/no-bare-strings-in-template -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationApi from 'dashboard/api/conversations';
import MessageApi from 'dashboard/api/inbox/message';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
      ],
      aiQuoteDraft: '',
      aiQuoteReason: '',
      aiQuoteError: '',
      aiQuoteTravelState: null,
      aiQuoteInternalQuote: null,
      aiQuotePersistedQuote: null,
      aiQuoteHint:
        'اضغط تجهيز لقراءة محادثة هذا العميل وإعداد مسودة خاصة بهذا العميل.',
      aiQuoteConversationId: null,
      aiQuoteInFlightConversationId: null,
      aiQuotePanelTitle: 'مسودة عرض AI',
      isAiQuoteDetailsExpanded: false,
      isAiQuoteInternalExpanded: false,
      isGeneratingAiQuoteDraft: false,
      isSendingAiQuoteDraft: false,
      isUpdatingAiQuoteAction: false,
      isRunningCompetitorCheck: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
    hasAiQuoteContent() {
      return !!(
        this.aiQuoteDraft ||
        this.aiQuoteError ||
        this.aiQuoteHint ||
        this.isGeneratingAiQuoteDraft ||
        this.aiQuoteTravelState ||
        this.aiQuoteInternalQuote ||
        this.aiQuotePersistedQuote
      );
    },
    aiQuoteSummaryText() {
      if (this.isGeneratingAiQuoteDraft) return 'جاري تجهيز مسودة العميل...';
      if (this.aiQuoteError) return 'يحتاج مراجعة أو إعادة تجهيز';
      if (this.aiQuoteDraft) return 'مسودة جاهزة للمراجعة';
      if (this.aiQuotePersistedQuote || this.aiQuoteTravelState) {
        return 'تحليل محفوظ لهذا العميل';
      }
      return 'اضغط تجهيز عند الحاجة';
    },
    aiQuoteSummaryMeta() {
      const state = this.aiQuoteTravelState || {};
      const parts = [
        state.travel_city,
        state.requested_hotel,
        state.check_in_date && state.check_out_date
          ? `${state.check_in_date} - ${state.check_out_date}`
          : state.check_in_date,
        state.number_of_rooms ? `${state.number_of_rooms} غرف` : null,
        state.adults_count ? `${state.adults_count} أشخاص` : null,
      ].filter(Boolean);
      return parts.join(' · ');
    },
  },
  watch: {
    conversationId() {
      this.onConversationChanged();
    },
    'currentChat.id'() {
      this.onConversationChanged();
    },
  },
  mounted() {
    window.addEventListener('armgold:open-ai-quote', this.onOpenAiQuotePanel);
    this.onConversationChanged();
  },
  beforeUnmount() {
    window.removeEventListener(
      'armgold:open-ai-quote',
      this.onOpenAiQuotePanel
    );
  },
  methods: {
    currentConversationKey() {
      return String(this.currentChat?.id || this.conversationId || '');
    },

    clearAiQuoteDraft() {
      this.aiQuoteDraft = '';
      this.aiQuoteReason = '';
      this.aiQuoteError = '';
      this.aiQuoteTravelState = null;
      this.aiQuoteInternalQuote = null;
      this.aiQuotePersistedQuote = null;
      this.aiQuoteHint =
        'اضغط تجهيز لقراءة محادثة هذا العميل وإعداد مسودة خاصة بهذا العميل.';
      this.aiQuoteConversationId = this.currentConversationKey();
      this.isSendingAiQuoteDraft = false;
      this.isUpdatingAiQuoteAction = false;
      this.isRunningCompetitorCheck = false;
      this.isAiQuoteDetailsExpanded = false;
      this.isAiQuoteInternalExpanded = false;
    },

    toggleAiQuoteDetails() {
      if (!this.hasAiQuoteContent && !this.isGeneratingAiQuoteDraft) {
        this.onGenerateAiQuoteDraft();
        this.isAiQuoteDetailsExpanded = true;
        return;
      }
      this.isAiQuoteDetailsExpanded = !this.isAiQuoteDetailsExpanded;
    },

    onOpenAiQuotePanel() {
      this.isAiQuoteDetailsExpanded = true;
      if (!this.hasAiQuoteContent && !this.isGeneratingAiQuoteDraft) {
        this.onGenerateAiQuoteDraft();
      }
    },

    shouldAutoGenerateAiQuoteDraft() {
      const labels = this.currentChat?.labels || [];
      return labels.some(label =>
        [
          'ai-quote-draft-ready',
          'جاهز-للتسعير',
          'طلب-سعر',
          'need-competitor-check',
          'needs-agent-approval',
          '03-مؤهل',
          'hot-lead',
        ].includes(label)
      );
    },

    async onConversationChanged() {
      this.clearAiQuoteDraft();
      const loaded = await this.loadPersistedAiQuoteDraft();
      if (!loaded && this.shouldAutoGenerateAiQuoteDraft()) {
        this.onGenerateAiQuoteDraft({ automatic: true });
      }
    },

    async loadPersistedAiQuoteDraft() {
      const requestConversationId = this.currentChat?.id;
      const requestConversationKey = this.currentConversationKey();
      if (!requestConversationId) return false;

      try {
        const { data } = await ConversationApi.getAiQuoteDraft(
          requestConversationId
        );
        if (this.currentConversationKey() !== requestConversationKey) {
          return false;
        }

        if (!data?.persisted_quote && !data?.travel_state) {
          return false;
        }

        this.aiQuotePersistedQuote = data.persisted_quote || null;
        this.aiQuoteTravelState = data.travel_state || null;
        this.aiQuoteInternalQuote = data.persisted_quote
          ? {
              status: data.persisted_quote.status,
              internal_note: data.persisted_quote.internal_note,
              agent_checklist: data.persisted_quote.agent_checklist,
              competitor_check_required:
                data.persisted_quote.competitor_check_required,
              competitor_check: data.persisted_quote.competitor_check,
            }
          : null;
        this.aiQuoteConversationId = requestConversationKey;
        this.aiQuoteHint = data.persisted_quote
          ? 'تم تحميل آخر مسودة AI محفوظة لهذا العميل.'
          : 'تم تحميل حالة AI المحفوظة لهذا العميل.';
        return true;
      } catch (error) {
        return false;
      }
    },

    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },

    async onGenerateAiQuoteDraft(options = {}) {
      const requestConversationId = this.currentChat.id;
      const requestConversationKey = this.currentConversationKey();

      if (
        this.isGeneratingAiQuoteDraft &&
        this.aiQuoteInFlightConversationId === requestConversationKey
      ) {
        return;
      }

      this.aiQuoteInFlightConversationId = requestConversationKey;
      this.aiQuoteConversationId = requestConversationKey;
      this.aiQuoteError = '';
      this.aiQuoteReason = '';
      this.aiQuoteHint = options.automatic
        ? 'جاري قراءة محادثة هذا العميل وتجهيز مسودة مخصصة...'
        : '';
      this.isGeneratingAiQuoteDraft = true;

      try {
        const { data } = await ConversationApi.generateAiQuoteDraft(
          requestConversationId
        );
        if (this.currentConversationKey() !== requestConversationKey) {
          return;
        }
        this.aiQuoteDraft = data.message || '';
        this.aiQuoteTravelState = data.travel_state || null;
        this.aiQuoteInternalQuote = data.internal_quote || null;
        this.aiQuotePersistedQuote = data.persisted_quote || null;
        this.aiQuoteReason = '';
        this.aiQuoteHint = '';

        if (!this.aiQuoteDraft) {
          this.aiQuoteError = this.safeAiQuoteReason(
            data.reason || data.private_note || 'No customer-ready draft yet.'
          );
        }
      } catch (error) {
        if (this.currentConversationKey() !== requestConversationKey) {
          return;
        }
        const serverError = error?.response?.data?.error;
        const status = error?.response?.status;
        const networkError =
          !error?.response && (error?.message || 'الطلب لم يصل للسيرفر.');
        this.aiQuoteError =
          serverError ||
          (status
            ? `تعذر تجهيز مسودة العرض. كود الخطأ: ${status}`
            : `تعذر تجهيز مسودة العرض. ${networkError} حدّث الصفحة ثم جرّب مرة ثانية.`);
        this.aiQuoteHint = '';
      } finally {
        if (this.currentConversationKey() === requestConversationKey) {
          this.isGeneratingAiQuoteDraft = false;
        }
        if (this.aiQuoteInFlightConversationId === requestConversationKey) {
          this.aiQuoteInFlightConversationId = null;
        }
      }
    },

    async onSendAiQuoteDraft() {
      const message = this.aiQuoteDraft.trim();
      if (!message) return;
      if (this.aiQuoteConversationId !== this.currentConversationKey()) {
        this.aiQuoteError =
          'هذه المسودة لا تخص المحادثة الحالية. اضغط تجهيز لتحديثها.';
        return;
      }

      const labels = this.currentChat.labels || [];
      if (
        labels.includes('need-competitor-check') &&
        !labels.includes('competitor-checked')
      ) {
        this.aiQuoteError = 'يلزم فحص المنافسين أولاً قبل إرسال السعر النهائي.';
        return;
      }
      if (
        /Mawasim|مواسيم|vendor|net price|margin|token|OTP|Gmail/i.test(message)
      ) {
        this.aiQuoteError =
          'المسودة تحتوي معلومات داخلية. استخدم إعادة تسعير قبل الإرسال.';
        return;
      }

      this.aiQuoteError = '';
      this.isSendingAiQuoteDraft = true;

      try {
        await MessageApi.create({
          conversationId: this.currentChat.id,
          message,
          private: false,
          contentAttributes: {
            ai_quote_approved: true,
            approved_by_agent_id: this.currentUser.id,
          },
        });
        this.aiQuoteDraft = '';
        this.aiQuoteReason = '';
        this.aiQuoteHint =
          'تم إرسال المسودة. اضغط تجهيز إذا احتجت مسودة جديدة لهذا العميل.';
        useAlert('تم إرسال المسودة بعد موافقة الموظف');
      } catch (error) {
        this.aiQuoteError =
          error?.response?.data?.error || 'Could not send approved draft.';
      } finally {
        this.isSendingAiQuoteDraft = false;
      }
    },

    async onApplyAiQuoteAction(action) {
      const actionMap = {
        approve: {
          add: ['agent-approved-quote', 'competitor-checked'],
          remove: [
            'agent-rejected-quote',
            'needs-reprice',
            'need-competitor-check',
            'ai-quote-blocked',
          ],
          alert: 'تم اعتماد العرض / Quote approved',
        },
        reject: {
          add: ['agent-rejected-quote'],
          remove: ['agent-approved-quote', 'quote-approved-sent'],
          alert: 'تم رفض العرض / Quote rejected',
        },
        reprice: {
          add: ['needs-reprice'],
          remove: ['agent-approved-quote', 'quote-approved-sent'],
          alert: 'تم طلب إعادة التسعير / Reprice requested',
        },
        competitor: {
          add: ['need-competitor-check'],
          remove: ['agent-approved-quote', 'competitor-checked'],
          alert: 'تم طلب فحص المنافسين / Competitor check needed',
        },
      };
      const config = actionMap[action];
      if (!config) return;

      this.isUpdatingAiQuoteAction = true;
      this.aiQuoteError = '';

      try {
        const currentLabels = this.currentChat.labels || [];
        const nextLabels = [
          ...currentLabels.filter(label => !config.remove.includes(label)),
          ...config.add,
        ].filter((label, index, labels) => labels.indexOf(label) === index);

        await ConversationApi.updateLabels(this.currentChat.id, nextLabels);
        this.currentChat.labels = nextLabels;
        useAlert(config.alert);
      } catch (error) {
        this.aiQuoteError =
          error?.response?.data?.error || 'Could not update quote action.';
      } finally {
        this.isUpdatingAiQuoteAction = false;
      }
    },

    async onRunCompetitorCheck() {
      const requestConversationId = this.currentChat.id;
      const requestConversationKey = this.currentConversationKey();
      this.isRunningCompetitorCheck = true;
      this.aiQuoteError = '';
      this.aiQuoteHint = 'جاري فحص أسعار المنافسين...';

      try {
        const { data } = await ConversationApi.runAiCompetitorCheck(
          requestConversationId
        );
        if (this.currentConversationKey() !== requestConversationKey) return;

        await this.loadPersistedAiQuoteDraft();
        this.aiQuoteHint =
          data.status === 'checked'
            ? 'تم فحص المنافسين وتحديث المسودة.'
            : 'لم يكتمل فحص المنافسين تلقائياً، راجع الملاحظة الداخلية.';
        useAlert(this.aiQuoteHint);
      } catch (error) {
        this.aiQuoteError =
          error?.response?.data?.error || 'تعذر فحص المنافسين تلقائياً.';
        this.aiQuoteHint = '';
      } finally {
        if (this.currentConversationKey() === requestConversationKey) {
          this.isRunningCompetitorCheck = false;
        }
      }
    },

    safeAiQuoteReason(reason) {
      const text = String(reason || '').trim();
      if (!text) return '';

      const hasInternalVendorText =
        /Vendor pricing|missing_search_fields|Mawasim|OTP|Gmail|token|net price|internal errors/i.test(
          text
        );

      if (!hasInternalVendorText) return text;

      return [
        'لا يمكن تجهيز سعر نهائي لأن بيانات الحجز ناقصة أو تحتاج تحقق يدوي.',
        'الإجراء: اسأل العميل عن الناقص فقط، ثم اضغط تجهيز مرة ثانية لفحص الموردين والمنافسين وحساب الهامش.',
        'لا ترسل سعر تقريبي قبل التحقق.',
      ].join('\n');
    },

    quoteOptions() {
      return (
        this.aiQuotePersistedQuote?.items ||
        this.aiQuoteInternalQuote?.options ||
        []
      );
    },

    visibleQuoteOptions() {
      return this.quoteOptions().slice(0, 3);
    },

    competitorCheck() {
      return this.aiQuoteInternalQuote?.competitor_check || null;
    },

    competitorStatusText() {
      const check = this.competitorCheck();
      if (!check) return '';
      if (check.status === 'checked') return 'تم فحص المنافسين';
      if (check.status === 'pending_live_check') return 'فحص المنافسين مطلوب';
      if (check.status === 'manual_required') return 'فحص المنافسين يدوي';
      return check.status || '';
    },

    compactWarnings() {
      const warnings = [];
      const check = this.competitorStatusText();

      if (check && check !== 'تم فحص المنافسين') {
        warnings.push(check);
      }

      const hasPendingTax = this.quoteOptions().some(option =>
        /confirm|تأكد|راجع/i.test(String(option.tax_status || ''))
      );
      const hasRoomOnly = this.quoteOptions().some(option =>
        /Room Only|بدون وجبات/i.test(String(option.meal_plan || ''))
      );

      if (hasPendingTax) warnings.push('راجع VAT قبل الإرسال');
      if (hasRoomOnly) warnings.push('العروض بدون إفطار');

      return warnings.slice(0, 3);
    },

    formatMoney(value, currency = 'SAR') {
      if (value === null || value === undefined || value === '') return '—';
      return `${Number(value).toLocaleString()} ${currency || 'SAR'}`;
    },

    aiQuoteLabel(key) {
      return {
        status: 'الحالة',
        request: 'الطلب',
        check: '✓',
        separator: '·',
        competitors: 'فحص المنافسين',
      }[key];
    },

    formatQuoteOptionPricing(option) {
      const nights = Number(option.nights || 0);
      const totalLabel =
        nights > 1 ? `إجمالي ${nights} ليالي` : 'إجمالي الفترة';
      return [
        `${totalLabel}: ${this.formatMoney(option.sell_price, option.currency)}`,
        option.sell_per_night
          ? `متوسط الليلة: ${this.formatMoney(
              option.sell_per_night,
              option.currency
            )}`
          : null,
        `تكلفة المورد: ${this.formatMoney(option.net_price, option.currency)}`,
        `الهامش: ${this.formatMoney(option.margin_amount, option.currency)}`,
        option.margin_rate
          ? `نسبة: ${Number(option.margin_rate).toFixed(2)}%`
          : null,
      ]
        .filter(Boolean)
        .join(' · ');
    },

    formatQuoteOptionCompactPricing(option) {
      return [
        `للعميل: ${this.formatMoney(option.sell_price, option.currency)}`,
        option.net_price
          ? `المورد: ${this.formatMoney(option.net_price, option.currency)}`
          : null,
        option.margin_amount
          ? `هامش: ${this.formatMoney(option.margin_amount, option.currency)}`
          : null,
      ]
        .filter(Boolean)
        .join(' · ');
    },

    formatQuoteOptionMeta(option) {
      return [
        option.distance_text,
        option.meal_plan,
        option.cancellation_policy,
        option.tax_status ? `VAT/رسوم: ${option.tax_status}` : null,
        option.breakfast_status ? `الإفطار: ${option.breakfast_status}` : null,
        option.extra_bed_status
          ? `Extra bed: ${option.extra_bed_status}`
          : null,
      ]
        .filter(Boolean)
        .join(' · ');
    },

    formatQuoteOptionCompactMeta(option) {
      return [
        option.distance_text,
        option.meal_plan,
        option.tax_status ? `VAT: ${option.tax_status}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
    },

    formatCompetitorSource(source) {
      return [source.name, source.status, source.kind]
        .filter(Boolean)
        .join(' · ');
    },
  },
};
</script>

<template>
  <div class="bg-n-background">
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <div class="ai-quote-draft-panel">
      <div class="flex items-center justify-between gap-2">
        <ContactDetailsItem compact :title="aiQuotePanelTitle" />
        <NextButton
          sm
          slate
          icon="i-lucide-sparkles"
          :is-loading="isGeneratingAiQuoteDraft"
          label="تجهيز"
          @click="onGenerateAiQuoteDraft"
        />
      </div>
      <button
        type="button"
        class="ai-quote-summary"
        :class="{
          'ai-quote-summary--ready': aiQuoteDraft,
          'ai-quote-summary--error': aiQuoteError,
        }"
        @click="toggleAiQuoteDetails"
      >
        <span class="ai-quote-summary-dot" />
        <span class="ai-quote-summary-copy">
          <strong>{{ aiQuoteSummaryText }}</strong>
          <small v-if="aiQuoteSummaryMeta">{{ aiQuoteSummaryMeta }}</small>
        </span>
        <span class="ai-quote-summary-action">
          {{ isAiQuoteDetailsExpanded ? 'إخفاء' : 'فتح' }}
        </span>
      </button>
      <div
        v-if="
          isAiQuoteDetailsExpanded &&
          (hasAiQuoteContent || isGeneratingAiQuoteDraft)
        "
        class="ai-quote-draft-body"
      >
        <p
          v-if="!aiQuoteDraft && !aiQuoteError && aiQuoteHint"
          class="ai-quote-draft-hint"
        >
          {{ aiQuoteHint }}
        </p>
        <textarea
          v-if="aiQuoteDraft"
          v-model="aiQuoteDraft"
          dir="auto"
          class="ai-quote-draft-textarea"
          rows="7"
        />
        <p v-if="aiQuoteError" class="ai-quote-draft-error">
          {{ aiQuoteError }}
        </p>
        <div
          v-if="
            aiQuoteTravelState || aiQuoteInternalQuote || aiQuotePersistedQuote
          "
          class="ai-quote-internal"
          dir="auto"
        >
          <div v-if="aiQuoteTravelState" class="ai-quote-internal-row">
            <strong>{{ aiQuoteLabel('request') }}</strong>
            <span>
              {{
                [
                  aiQuoteTravelState.travel_city,
                  aiQuoteTravelState.requested_hotel,
                  aiQuoteTravelState.view_preference,
                  aiQuoteTravelState.check_in_date,
                  aiQuoteTravelState.check_out_date,
                ]
                  .filter(Boolean)
                  .join(' · ') || '—'
              }}
            </span>
          </div>
          <div v-if="compactWarnings().length" class="ai-quote-warning-list">
            <div
              v-for="warning in compactWarnings()"
              :key="warning"
              class="ai-quote-warning"
            >
              {{ warning }}
            </div>
          </div>
          <div v-if="visibleQuoteOptions().length" class="ai-quote-options">
            <div
              v-for="option in visibleQuoteOptions()"
              :key="`${option.position}-${option.hotel_name}`"
              class="ai-quote-option"
            >
              <strong>{{
                [option.position, option.hotel_name].filter(Boolean).join('. ')
              }}</strong>
              <span>{{ formatQuoteOptionCompactPricing(option) }}</span>
              <span v-if="formatQuoteOptionCompactMeta(option)">
                {{ formatQuoteOptionCompactMeta(option) }}
              </span>
            </div>
          </div>
          <button
            v-if="
              aiQuoteInternalQuote?.internal_note ||
              aiQuoteInternalQuote?.agent_checklist?.length ||
              competitorCheck()
            "
            type="button"
            class="ai-quote-internal-toggle"
            @click="isAiQuoteInternalExpanded = !isAiQuoteInternalExpanded"
          >
            {{
              isAiQuoteInternalExpanded
                ? 'إخفاء التفاصيل الداخلية'
                : 'تفاصيل داخلية'
            }}
          </button>
          <div
            v-if="isAiQuoteInternalExpanded"
            class="ai-quote-internal-details"
          >
            <div v-if="aiQuoteTravelState" class="ai-quote-internal-row">
              <strong>{{ aiQuoteLabel('status') }}</strong>
              <span>
                {{ aiQuoteTravelState.travel_request_status || '—' }}
                <template v-if="aiQuoteTravelState.ai_intent">
                  {{ aiQuoteLabel('separator') }}
                  {{ aiQuoteTravelState.ai_intent }}
                </template>
              </span>
            </div>
            <div
              v-if="aiQuoteInternalQuote?.internal_note"
              class="ai-quote-note"
            >
              {{ aiQuoteInternalQuote.internal_note }}
            </div>
            <div v-if="aiQuoteInternalQuote?.agent_checklist?.length">
              <div
                v-for="item in aiQuoteInternalQuote.agent_checklist"
                :key="item"
                class="ai-quote-check"
              >
                {{ aiQuoteLabel('check') }} {{ item }}
              </div>
            </div>
            <div v-if="competitorCheck()" class="ai-quote-competitors">
              <strong>{{ aiQuoteLabel('competitors') }}</strong>
              <span>{{ competitorCheck().status }}</span>
              <span v-if="competitorCheck().guidance">
                {{ competitorCheck().guidance }}
              </span>
              <div
                v-for="source in competitorCheck().sources || []"
                :key="source.name"
                class="ai-quote-competitor-source"
              >
                {{ formatCompetitorSource(source) }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="aiQuoteDraft" class="flex justify-end">
          <NextButton
            sm
            teal
            icon="i-lucide-send"
            :is-loading="isSendingAiQuoteDraft"
            :disabled="aiQuoteConversationId !== currentConversationKey()"
            label="إرسال بعد المراجعة"
            @click="onSendAiQuoteDraft"
          />
        </div>
        <div class="ai-quote-actions">
          <NextButton
            xs
            teal
            icon="i-lucide-check"
            :is-loading="isUpdatingAiQuoteAction"
            label="اعتماد / Approve"
            @click="onApplyAiQuoteAction('approve')"
          />
          <NextButton
            xs
            ruby
            icon="i-lucide-x"
            :is-loading="isUpdatingAiQuoteAction"
            label="رفض / Reject"
            @click="onApplyAiQuoteAction('reject')"
          />
          <NextButton
            xs
            amber
            icon="i-lucide-refresh-cw"
            :is-loading="isUpdatingAiQuoteAction"
            label="إعادة تسعير / Reprice"
            @click="onApplyAiQuoteAction('reprice')"
          />
          <NextButton
            xs
            slate
            icon="i-lucide-search"
            :is-loading="isRunningCompetitorCheck"
            label="فحص منافسين / Competitors"
            @click="onRunCompetitorCheck"
          />
        </div>
      </div>
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />
  </div>
</template>

<style scoped>
.ai-quote-draft-panel {
  padding: var(--space-small);
  border-top: 1px solid var(--color-border-light);
}

.ai-quote-draft-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
  margin-top: var(--space-smaller);
}

.ai-quote-summary {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  width: 100%;
  gap: var(--space-smaller);
  margin-top: var(--space-smaller);
  padding: var(--space-smaller) var(--space-small);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-small);
  background: var(--color-background-soft);
  color: var(--color-body);
  text-align: start;
  cursor: pointer;
}

.ai-quote-summary:hover {
  background: var(--color-background);
}

.ai-quote-summary-dot {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 999px;
  background: var(--s-400);
}

.ai-quote-summary--ready .ai-quote-summary-dot {
  background: var(--g-500);
}

.ai-quote-summary--error .ai-quote-summary-dot {
  background: var(--r-500);
}

.ai-quote-summary-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.125rem;
}

.ai-quote-summary-copy strong,
.ai-quote-summary-copy small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-quote-summary-copy strong {
  font-size: var(--font-size-micro);
  font-weight: var(--font-weight-medium);
}

.ai-quote-summary-copy small {
  color: var(--color-body-muted);
  font-size: var(--font-size-micro);
}

.ai-quote-summary-action {
  color: var(--color-body-muted);
  font-size: var(--font-size-micro);
}

.ai-quote-draft-textarea {
  width: 100%;
  min-height: 9rem;
  resize: vertical;
  padding: var(--space-small);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-small);
  background: var(--color-background);
  color: var(--color-body);
  font-size: var(--font-size-mini);
  line-height: 1.6;
}

.ai-quote-draft-reason {
  color: var(--color-body-light);
  font-size: var(--font-size-micro);
}

.ai-quote-draft-error {
  color: var(--r-500);
  font-size: var(--font-size-micro);
}

.ai-quote-draft-hint {
  color: var(--s-500);
  font-size: var(--font-size-micro);
  line-height: 1.5;
}

.ai-quote-internal {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
  padding: var(--space-small);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-small);
  background: var(--color-background-soft);
  color: var(--color-body);
  font-size: var(--font-size-micro);
  line-height: 1.5;
}

.ai-quote-internal-row {
  display: grid;
  grid-template-columns: 4.5rem minmax(0, 1fr);
  gap: var(--space-smaller);
}

.ai-quote-note {
  color: var(--y-700);
}

.ai-quote-warning-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.ai-quote-warning {
  display: inline-flex;
  width: fit-content;
  padding: 0.125rem 0.375rem;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.16);
  color: var(--y-700);
  font-size: var(--font-size-micro);
}

.ai-quote-check {
  color: var(--s-700);
}

.ai-quote-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
}

.ai-quote-option {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: var(--space-smaller);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-small);
  background: var(--color-background);
}

.ai-quote-option strong {
  color: var(--color-body);
}

.ai-quote-option span {
  color: var(--color-body-muted);
}

.ai-quote-internal-toggle {
  width: fit-content;
  color: var(--color-woot);
  font-size: var(--font-size-micro);
  text-align: start;
}

.ai-quote-internal-details {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
  max-height: 16rem;
  overflow: auto;
  padding-top: var(--space-smaller);
  border-top: 1px solid var(--color-border-light);
}

.ai-quote-competitors {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding-top: var(--space-smaller);
  border-top: 1px solid var(--color-border-light);
  color: var(--color-body);
}

.ai-quote-competitor-source {
  color: var(--color-body-muted);
}

.ai-quote-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-smaller);
}

.ai-quote-actions :deep(button) {
  min-width: 0;
}
</style>
